import { ArrowLeftIcon } from '@heroicons/react/20/solid';
import { Link } from 'react-router';
import { useBrandConfig } from '~/config/branding';

const LAST_UPDATED_DATE = 'December 14, 2025';

export const Privacy = () => {
  // [MCV-1728] Rights requests and the contact block go to the visiting brand's own
  // info@ inbox (the one configured for the Contact page and the signed-out footer;
  // the signed-in footer uses support@, MCV-1779), not a marketplaceai.com address.
  const { contactEmail } = useBrandConfig();

  return (
    <div className="text-left bg-white py-12 sm:py-16 flex-grow">
      <div className="mx-auto max-w-4xl px-6 lg:px-8">
        <Link
          to="/"
          className="inline-flex items-center gap-1 text-sm font-medium text-primary-50 hover:text-primary-t-100 mb-6"
        >
          <ArrowLeftIcon className="size-4" />
          Back to Home
        </Link>
        <h1 className="text-2xl font-semibold text-grey-100 sm:text-3xl">Privacy Policy</h1>
        <div className="mt-8 space-y-6 text-sm/6 text-grey-400 text-left">
          <p>Last updated: {LAST_UPDATED_DATE}</p>
          <div>
            <h3 className="text-xl font-semibold text-grey-100">1. Introduction</h3>
            <p className="mt-2 text-grey-400">
              This Privacy Policy explains how Marketplace AI, Inc. collects, uses, discloses, and
              safeguards information across its integrated Platform, including HealthcareLX,
              MedCredVault, and related services.
            </p>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-grey-100">2. Information We Collect</h3>
            <div className="mt-2 text-grey-400 space-y-4">
              <div>
                <h4 className="font-semibold text-grey-100">2.1 Information You Provide</h4>
                <ul className="list-disc pl-5 mt-1">
                  <li>Contact information (name, email, phone)</li>
                  <li>Professional information (licenses, certifications, CVs)</li>
                  <li>Employer or facility information</li>
                  <li>Communications and messages</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-grey-100">2.2 Automatically Collected Data</h4>
                <ul className="list-disc pl-5 mt-1">
                  <li>IP address, device type, browser</li>
                  <li>Usage logs, timestamps, feature interactions</li>
                  <li>Cookies and similar tracking technologies</li>
                </ul>
              </div>
              <div>
                <h4 className="font-semibold text-grey-100">2.3 Sensitive and Regulated Data</h4>
                <ul className="list-disc pl-5 mt-1">
                  <li>Credentialing documents</li>
                  <li>Employment verification data</li>
                  <li>Limited health-related data where permitted</li>
                </ul>
              </div>
            </div>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-grey-100">3. How We Use Information</h3>
            <div className="mt-2 text-grey-400">
              <p>We use data to:</p>
              <ul className="list-disc pl-5 mt-1">
                <li>Operate and improve the Platform</li>
                <li>Enable staffing, credentialing, and compliance workflows</li>
                <li>Perform analytics, AI modeling, and fraud detection</li>
                <li>Enforce platform rules and agreements</li>
                <li>Communicate with users</li>
                <li>Comply with legal obligations</li>
              </ul>
            </div>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-grey-100">4. Integrated Platform Data Use</h3>
            <div className="mt-2 text-grey-400 space-y-2">
              <p>
                Because Marketplace AI operates a unified platform, data provided in one module may
                be used in another <strong>for legitimate platform purposes</strong> (e.g.,
                credential reuse during job applications).
              </p>
              <p>
                We may share <strong>data</strong> for analytics, benchmarking, and business
                intelligence.
              </p>
            </div>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-grey-100">5. Data Sharing</h3>
            <div className="mt-2 text-grey-400">
              <p>We may share information:</p>
              <ul className="list-disc pl-5 mt-1">
                <li>Internally across Platform modules</li>
                <li>With vetted service providers under confidentiality obligations</li>
                <li>In connection with mergers, acquisitions, or financing</li>
                <li>To comply with law or protect rights and safety</li>
              </ul>
            </div>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-grey-100">6. HIPAA and Healthcare Data</h3>
            <div className="mt-2 text-grey-400">
              <p>Where applicable:</p>
              <ul className="list-disc pl-5 mt-1">
                <li>Certain users may be Covered Entities or Business Associates</li>
                <li>A separate Business Associate Agreement (BAA) may be required</li>
                <li>
                  Absent a BAA, users agree not to upload Protected Health Information unless
                  legally permitted
                </li>
              </ul>
            </div>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-grey-100">7. Data Security</h3>
            <p className="mt-2 text-grey-400">
              We employ administrative, technical, and physical safeguards, including encryption and
              access controls. No system is perfectly secure; users remain responsible for
              credential security.
            </p>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-grey-100">8. Data Retention</h3>
            <div className="mt-2 text-grey-400">
              <p>We retain information as long as necessary to:</p>
              <ul className="list-disc pl-5 mt-1">
                <li>Provide services</li>
                <li>Comply with law</li>
                <li>Resolve disputes</li>
                <li>Enforce agreements</li>
              </ul>
            </div>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-grey-100">9. Your Rights</h3>
            <div className="mt-2 text-grey-400">
              <p>Depending on jurisdiction, you may have rights to:</p>
              <ul className="list-disc pl-5 mt-1">
                <li>Access</li>
                <li>Correction</li>
                <li>Deletion</li>
                <li>Restriction</li>
                <li>Data portability</li>
              </ul>
              <p className="mt-2">
                Requests may be submitted to{' '}
                <a
                  href={`mailto:${contactEmail}`}
                  className="text-primary-50 hover:text-primary-t-100"
                >
                  {contactEmail}
                </a>
                .
              </p>
            </div>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-grey-100">10. Children’s Privacy</h3>
            <p className="mt-2 text-grey-400">
              The Platform is not intended for individuals under 18. We do not knowingly collect
              data from minors.
            </p>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-grey-100">11. Policy Updates</h3>
            <p className="mt-2 text-grey-400">
              We may update this Privacy Policy periodically. Continued use after updates
              constitutes acceptance.
            </p>
          </div>
          <div>
            <h3 className="text-xl font-semibold text-grey-100">12. Contact Information</h3>
            <div className="mt-2 text-grey-400">
              <p>Marketplace AI, Inc.</p>
              <p>P.O. Box 332</p>
              <p>Sandusky, OH 44871</p>
              <p>
                Email:{' '}
                <a
                  href={`mailto:${contactEmail}`}
                  className="text-primary-50 hover:text-primary-t-100"
                >
                  {contactEmail}
                </a>
              </p>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
